// DispatchDeck — Team Notes (TASK-9619 slice 1, TASK-9835 slice 2)
// ============================================================================
// `window.TeamNotes` — the Notes button beside the DispatchDeck logo (Chris
// 2026-10-05 / decision 2026-10-12 bd-notesorb: BY THE LOGO). Click it for
// your notes; pick who gets one on the To: line (or type `@` in the text) —
// they see the SAME thread (a link, never a copy), replies stay in one thread
// and reach everyone on it. Hover peeks the newest unread note.
//
// SLICE 2 (TASK-9835, Chris + Lacey 2026-10-13):
//   A. The button sits in a tools row under the logo (#dd-brand-tools) so it
//      never covers the logo's corner. It is the golden orb again (Chris
//      2026-10-19, crew asked for it back): amber radial gradient, sticky_note_2
//      glyph, white unread count, glow while unread. TASK-9823's phone button
//      appends to the same row as a plain outline .dd-brand-tool (same height).
//   B. "Who is this going to" is impossible to miss: a To: line ABOVE the text
//      box that is never blank ("Just me (private)" until someone is picked),
//      picked people as BLUE chips (blue = recognized person), "+ Add person"
//      opens the picker; `@` in the text is a shortcut into the same picker.
//      Typo guard: an `@word` that never became a chip blocks Send
//      ("Nobody named 'lacy'…"); `bob@x.com` is not a mention.
//   C. @everyone — first in the picker; confirmed with the count before send;
//      resolved SERVER-SIDE in manageNote (active notes-holders at send time).
//
// Data: tenants/{tid}/notes — one THREAD doc per note (shape in
// functions/src/services/teamNotes.js). Writes: ONLY the `manageNote` callable
// (rules are participant-read / write:false); the client never writes a
// recipient list. The list query is `participantUids array-contains uid`, which
// is exactly what the read rule proves — an unscoped query is denied.
//
// ACCESS = MODULES, NOT ROLES: the `notes` entry in members.modules (owner via
// tenants.ownerId). Off by default; the owner ticks it per login (Roster →
// staff login). Hidden entirely without it. Driver-PIN / link / public
// sessions never see it. Demo: the anon session sees its own seeded note.
//
// Phones: the main app has no top bar (the CP has one), so the phone entry is a
// "Notes" row in the More sheet (#mobile-notes-btn) plus an unread dot on the
// More button; the panel goes full-screen (100dvh via .modal-fullscreen).
//
// NOT in this slice: # record chips, groups, texting on a mention (TASK-9619 row).
//
// LOADING (Pitfall #1): imported ONCE by app.js. Anything else calls
// `window.TeamNotes?.open()` — never an import.
// ============================================================================

import {
  auth,
  db,
  functions,
  httpsCallable,
  onAuthStateChanged,
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
} from './firebase-config.js?v=1868';

(function () {
  'use strict';

  const MODULE = 'notes';
  const INACTIVE = ['suspended', 'removed', 'revoked', 'inactive', 'pending'];
  const TEXT_MAX = 2000;
  const EDIT_WINDOW_MS = 10 * 60 * 1000;
  const TABS = [['forme', 'For me'], ['sent', 'Sent'], ['all', 'All']];
  // The "Everyone" chip. Lives in S.mentions like a person (so drafts carry
  // it) but is never sent as a uid — send() turns it into `everyone: true`.
  const EVERYONE = '*everyone';
  const EVERYONE_NAME = 'Everyone';

  const S = {
    key: null,            // `${tenant}|${uid}` the listener belongs to
    unsub: null,
    threads: [],
    ready: false,         // first snapshot landed (arrivals after this toast)
    seen: new Set(),      // `${id}:${lastMessageAtMs}` already shown / present
    pickable: [],         // [{uid, name}] teammates who can be mentioned
    names: new Map(),     // uid -> display name (members + owner)
    view: 'list',         // list | compose | thread
    tab: 'forme',
    showArchived: false,
    open: null,           // open thread id
    editing: null,        // message id being edited
    busy: false,
    mentions: new Map(),  // uid -> name for the composer in view
    pickIdx: 0,
    peek: null,
    drafts: {},           // ctx ('compose' | thread id) -> {text, mentions:[[uid,name]], at}
    storeKey: null,       // localStorage key the drafts belong to (tenant + uid)
    // TASK-9835 D — the "Also text it" preview for the composer in view: key
    // (ctx + audience), the server's answer, and whether the box is ticked.
    tp: { key: null, data: null, checked: false, seq: 0, timer: null },
  };

  const esc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  const toast = (m, t, action) => (window.showToast ? window.showToast(m, t, action || null) : console.log('[TeamNotes]', m));

  function tenantId() {
    const t = window.AppState?.tenant;
    return t === 'demo-tenant' ? 'demo' : (t || null);
  }
  function isDemo() { return !!window.AppState?.isDemo || tenantId() === 'demo'; }
  function me() { return (window.AppState?.user || auth.currentUser || {}).uid || null; }
  function isDriverContext() {
    return !!(window.AppState?.driverModePin || window.AppState?.driverMode || window.AppState?.accessToken || window.AppState?.isPublicAccess);
  }
  /** Mirror of the rules + callable gate (affordance only; the server enforces). */
  function canUse() {
    if (isDriverContext()) return false;
    const user = window.AppState?.user || auth.currentUser;
    if (!user || !tenantId()) return false;
    if (isDemo()) return true;
    if (user.isAnonymous) return false;
    if (window.AppState?.tenantData?.ownerId && window.AppState.tenantData.ownerId === user.uid) return true;
    const ma = window.AppState?.memberAccess;
    if (!ma || !Array.isArray(ma.modules)) return false;
    if (INACTIVE.includes(ma.status)) return false;
    if (ma.readOnly === true) return false;
    return ma.modules.includes(MODULE);
  }

  // --------------------------------------------------------------------------
  // Thread helpers
  // --------------------------------------------------------------------------
  function parse(d) {
    const x = d.data() || {};
    return {
      id: d.id,
      authorUid: x.authorUid || null,
      authorName: x.authorName || '',
      participantUids: Array.isArray(x.participantUids) ? x.participantUids : [],
      participants: x.participants || {},
      messages: Array.isArray(x.messages) ? x.messages : [],
      lastMessageAtMs: Number(x.lastMessageAtMs) || 0,
      lastMessageUid: x.lastMessageUid || null,
      lastMessageName: x.lastMessageName || '',
      preview: x.preview || '',
      readAtMs: x.readAtMs || {},
      archivedBy: x.archivedBy || {},
      audience: x.audience === 'everyone' ? 'everyone' : 'picked',
    };
  }
  const isArchived = (t) => !!(t.archivedBy && t.archivedBy[me()]);
  const isUnread = (t) => !isArchived(t) && t.lastMessageUid !== me() && (Number(t.readAtMs[me()]) || 0) < t.lastMessageAtMs;
  const unreadThreads = () => S.threads.filter(isUnread);
  const find = (id) => S.threads.find((t) => t.id === id) || null;
  function nameOf(t, uid) {
    if (uid === me()) return 'You';
    return (t && t.participants && t.participants[uid] && t.participants[uid].name) || S.names.get(uid) || 'Teammate';
  }
  function others(t) {
    return t.participantUids.filter((u) => u !== me()).map((u) => nameOf(t, u));
  }
  /** Who a thread reaches, for the list row / thread title. */
  function whoLine(t) {
    if (t.audience === 'everyone') return `Everyone (${t.participantUids.length})`;
    const who = others(t);
    return who.length ? who.join(', ') : 'Only you';
  }

  // --------------------------------------------------------------------------
  // Mention text helpers (pure; exercised by tests/task9835-notes-s2.unit.cjs)
  // --------------------------------------------------------------------------
  /** Every `@word` that starts a word (so `bob@x.com` is not one). */
  function mentionTokens(text) {
    const out = [];
    const re = /(^|[\s(\[])@([^\s@]+)/g;
    let m;
    while ((m = re.exec(String(text || '')))) {
      const word = m[2].replace(/[.,;:!?)\]]+$/, '');
      if (word) out.push({ at: m.index + m[1].length, word });
    }
    return out;
  }
  /** Typed `@word`s that no picked chip covers (full name or first name, case-insensitive). */
  function unmatchedMentions(text, names) {
    const s = String(text || '');
    const cands = [];
    for (const n of names || []) {
      const full = String(n || '').trim().toLowerCase();
      if (!full) continue;
      cands.push(full);
      const first = full.split(/\s+/)[0];
      if (first && first !== full) cands.push(first);
    }
    return mentionTokens(s).filter(({ at }) => {
      const rest = s.slice(at + 1).toLowerCase();
      return !cands.some((n) => rest.startsWith(n) && !/[a-z0-9_]/.test(rest.charAt(n.length)));
    }).map((t) => t.word);
  }
  /** Remove the first `@Name` (and one trailing space) from a textarea. */
  function stripMention(ta, name) {
    if (!ta || !name) return;
    const re = new RegExp('(^|\\s)@' + String(name).replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '(?![A-Za-z0-9_]) ?', 'i');
    ta.value = ta.value.replace(re, '$1');
  }
  function visibleThreads() {
    const mine = me();
    return S.threads.filter((t) => {
      if (isArchived(t) && !(S.tab === 'all' && S.showArchived)) return false;
      if (S.tab === 'forme') return t.authorUid !== mine;
      if (S.tab === 'sent') return t.authorUid === mine;
      return true;
    });
  }
  function when(ms) {
    if (!ms) return '';
    const d = new Date(ms);
    const diff = Date.now() - ms;
    if (diff < 60000) return 'now';
    if (diff < 3600000) return Math.floor(diff / 60000) + 'm';
    if (diff < 86400000 && d.getDate() === new Date().getDate()) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    if (diff < 6 * 86400000) return d.toLocaleDateString('en-US', { weekday: 'short' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  function whenLong(ms) {
    if (!ms) return '';
    return new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }
  /** Escaped text with @Name chips for the thread's participants and line breaks. */
  function renderText(t, text) {
    let html = esc(text);
    const names = Object.values(t.participants || {}).map((p) => p && p.name).filter(Boolean)
      .concat(t.audience === 'everyone' ? [EVERYONE_NAME] : [])
      .sort((a, b) => b.length - a.length);
    for (const n of names) {
      const token = '@' + esc(n);
      html = html.split(token).join(`<span class="dd-mention">${token}</span>`);
    }
    return html.replace(/\n/g, '<br>');
  }

  // --------------------------------------------------------------------------
  // Drafts that outlive the panel (verify lane 2026-10-12: closing the panel
  // lost a half-written new note). The composer in view is captured on every
  // keystroke, pick and view change into S.drafts (memory: survives close /
  // reopen) and mirrored to localStorage keyed by tenant + uid (survives a
  // reload). Cleared for that ctx the moment it is sent. Storage can be
  // missing or throw (private window, blocked site data) — memory still works.
  // --------------------------------------------------------------------------
  const DRAFT_MAX_AGE_MS = 30 * 86400000;
  const DRAFT_MAX = 25;
  function draftStoreKey(tid, uid) { return `ddNotesDraft:${tid}:${uid}`; }
  /** Parse a stored blob into {view, open, drafts}; anything odd is dropped. */
  function parseStore(raw, nowMs) {
    const out = { view: 'list', open: null, drafts: {} };
    let o = null;
    try { o = JSON.parse(raw); } catch (e) { return out; }
    if (!o || typeof o !== 'object' || o.v !== 1) return out;
    if (o.view === 'compose' || o.view === 'thread') out.view = o.view;
    if (typeof o.open === 'string' && o.open.length <= 200) out.open = o.open;
    if (out.view === 'thread' && !out.open) out.view = 'list';
    if (out.view !== 'thread') out.open = null;
    const d = o.drafts && typeof o.drafts === 'object' ? o.drafts : {};
    const rows = Object.entries(d).filter(([ctx, x]) => typeof ctx === 'string' && ctx.length <= 200 && x && typeof x.text === 'string'
      && (Number(x.at) || 0) > nowMs - DRAFT_MAX_AGE_MS)
      .sort((a, b) => (Number(b[1].at) || 0) - (Number(a[1].at) || 0)).slice(0, DRAFT_MAX);
    for (const [ctx, x] of rows) {
      const mentions = Array.isArray(x.mentions)
        ? x.mentions.filter((m) => Array.isArray(m) && typeof m[0] === 'string' && typeof m[1] === 'string').slice(0, 20).map((m) => [m[0].slice(0, 128), m[1].slice(0, 120)])
        : [];
      if (!x.text.trim() && !mentions.length) continue;
      out.drafts[ctx] = { text: x.text.slice(0, TEXT_MAX), mentions, at: Number(x.at) || nowMs };
    }
    return out;
  }
  function serializeStore() {
    const view = S.view === 'thread' || S.view === 'compose' ? S.view : 'list';
    return JSON.stringify({ v: 1, view, open: view === 'thread' ? S.open : null, drafts: S.drafts });
  }
  function loadStore(tid, uid) {
    S.storeKey = draftStoreKey(tid, uid);
    let raw = null;
    try { raw = window.localStorage.getItem(S.storeKey); } catch (e) { raw = null; }
    const st = parseStore(raw, Date.now());
    S.drafts = st.drafts;
    S.view = st.view;
    S.open = st.open;
    loadMentions(ctxOf());
  }
  function persist() {
    if (!S.storeKey) return;
    try {
      if (!Object.keys(S.drafts).length && S.view === 'list') window.localStorage.removeItem(S.storeKey);
      else window.localStorage.setItem(S.storeKey, serializeStore());
    } catch (e) { /* storage unavailable: the memory copy still holds */ }
  }
  /** The ctx of the composer in view ('compose' or the open thread id), or null. */
  function ctxOf() { return S.view === 'thread' ? S.open : (S.view === 'compose' ? 'compose' : null); }
  function setDraft(ctx, text, mentions) {
    if (!ctx) return;
    if (!String(text || '').trim() && !(mentions && mentions.length)) delete S.drafts[ctx];
    else S.drafts[ctx] = { text: String(text || '').slice(0, TEXT_MAX), mentions: mentions || [], at: Date.now() };
  }
  /** Write the live composer (text + @ chips) into S.drafts, then storage. */
  function captureComposer() {
    const ctx = ctxOf();
    const ta = activeTextarea();
    if (ctx && ta && (ta.dataset.ctx || '') === ctx) setDraft(ctx, ta.value, [...S.mentions.entries()]);
    persist();
  }
  /** Switch the chips to the ones saved with this ctx's draft. */
  function loadMentions(ctx) { S.mentions = new Map(((ctx && S.drafts[ctx]) || {}).mentions || []); }

  // --------------------------------------------------------------------------
  // Listener
  // --------------------------------------------------------------------------
  function start() {
    const user = auth.currentUser;
    const tid = tenantId();
    if (!user || !tid) { stop(); return; }
    const ok = canUse();
    const key = `${tid}|${user.uid}|${ok ? 1 : 0}`;
    if (S.key === key) return;
    stop();
    S.key = key;
    injectCss();
    if (!ok) { showEntry(false); return; }
    ensureOrb();
    showEntry(true);
    loadStore(tid, user.uid);
    loadPeople();
    const q = query(collection(db, 'tenants', tid, 'notes'), where('participantUids', 'array-contains', user.uid));
    S.unsub = onSnapshot(q, (snap) => {
      S.threads = snap.docs.map(parse).sort((a, b) => b.lastMessageAtMs - a.lastMessageAtMs);
      const arrivals = [];
      for (const t of S.threads) {
        const k = `${t.id}:${t.lastMessageAtMs}`;
        if (!S.seen.has(k)) {
          S.seen.add(k);
          if (S.ready && isUnread(t) && !(S.open === t.id && isPanelOpen())) arrivals.push(t);
        }
      }
      S.ready = true;
      render();
      if (arrivals.length === 1) {
        const t = arrivals[0];
        toast(`${nameOf(t, t.lastMessageUid)}: ${t.preview}`, 'info', { label: 'Open', callback: () => openThread(t.id) });
      } else if (arrivals.length > 1) {
        toast(`${arrivals.length} new notes`, 'info', { label: 'Open', callback: () => open() });
      }
    }, (err) => {
      console.warn('[TeamNotes] listener error:', err?.code || err?.message || err);
      // Access revoked mid-session (module unticked, login removed): the rules
      // now deny the query. Drop every cached thread and close the panel so
      // nothing private stays on screen (COD Code Reviewer).
      if (/permission-denied/i.test(String(err?.code || err?.message || ''))) stop();
    });
  }

  function stop() {
    if (S.unsub) { try { S.unsub(); } catch (e) { /* ignore */ } }
    S.unsub = null;
    S.key = null;
    S.threads = [];
    S.ready = false;
    S.seen.clear();
    S.pickable = [];
    S.names.clear();
    S.open = null;
    S.editing = null;
    S.mentions.clear();
    S.drafts = {};
    S.storeKey = null;
    S.view = 'list';
    hidePeek();
    hidePick();
    const m = document.getElementById('dd-notes-modal');
    if (m) m.classList.add('hidden');
    const body = document.getElementById('dd-notes-body');
    if (body) body.innerHTML = '';
    showEntry(false);
    renderOrb();
  }

  /** Remove every stored notes draft on this browser except keepUid's own
   *  (null = all). Shared shop PC: sign-out, or a straight switch to another
   *  login, leaves no one else's unsent text in localStorage (COD Adversary). */
  function sweepDrafts(keepUid) {
    try {
      const ls = window.localStorage;
      const doomed = [];
      for (let i = 0; i < ls.length; i++) {
        const k = ls.key(i);
        if (k && k.startsWith('ddNotesDraft:') && !(keepUid && k.endsWith(':' + keepUid))) doomed.push(k);
      }
      doomed.forEach((k) => ls.removeItem(k));
    } catch (e) { /* storage unavailable: nothing stored to sweep */ }
  }

  onAuthStateChanged(auth, (u) => {
    if (!u) {
      // Signed out: unsent drafts don't stay on a shared PC. (A reload never
      // passes through here — auth restores the user before the first event.)
      sweepDrafts(null);
      stop();
      return;
    }
    sweepDrafts(u.uid);
    if (S.key && !S.key.includes('|' + u.uid + '|')) stop();
  });

  // Teammates who can be mentioned: ACTIVE members holding `notes` (not
  // read-only link members) plus the owner via tenants.ownerId. members/* is
  // readable by any tenant member; the server re-checks every target live.
  async function loadPeople() {
    const tid = tenantId();
    const mine = me();
    S.pickable = [];
    S.names.clear();
    if (!tid || isDemo()) return;
    const ownerId = window.AppState?.tenantData?.ownerId || null;
    try {
      const snap = await getDocs(collection(db, 'tenants', tid, 'members'));
      snap.docs.forEach((d) => {
        const m = d.data() || {};
        const name = m.displayName || m.email || 'Teammate';
        S.names.set(d.id, name);
        if (d.id === mine) return;
        const active = !INACTIVE.includes(m.status) && m.readOnly !== true;
        const holds = d.id === ownerId || (Array.isArray(m.modules) && m.modules.includes(MODULE));
        if (active && holds) S.pickable.push({ uid: d.id, name });
      });
    } catch (e) {
      console.warn('[TeamNotes] members load failed:', e?.code || e?.message || e);
    }
    if (ownerId && ownerId !== mine && !S.pickable.some((p) => p.uid === ownerId)) {
      const name = S.names.get(ownerId) || window.AppState?.tenantData?.ownerName || 'Owner';
      S.names.set(ownerId, name);
      S.pickable.push({ uid: ownerId, name });
    }
    S.pickable.sort((a, b) => a.name.localeCompare(b.name));
    if (isPanelOpen()) renderPanel();
  }

  // --------------------------------------------------------------------------
  // Calls
  // --------------------------------------------------------------------------
  // TASK-10112 — wait as long as the server may run (manageNote timeoutSeconds 120: "Also text it" sends).
  const _call = httpsCallable(functions, 'manageNote', { timeout: 130000 });
  async function call(payload) {
    const res = await _call({ ...payload, tenantId: tenantId() });
    return res?.data || {};
  }
  function friendly(err) {
    const m = err?.message || '';
    if (!m || /internal/i.test(err?.code || '')) return 'Could not save the note. Try again.';
    return m.replace(/^FirebaseError:\s*/, '');
  }

  // --------------------------------------------------------------------------
  // Notes button under the logo (desktop sidebar; also inside the phone drawer)
  // Lives in #dd-brand-tools, a right-aligned row BELOW the logo image, so it
  // never overlaps the logo at any sidebar width (slice 1 pinned it to the
  // logo block's corner — Chris's screenshot). The button itself is the golden
  // orb (Chris 2026-10-19). Other chrome (TASK-9823's phone button) appends to
  // the same row as an outline .dd-brand-tool: `document.getElementById('dd-brand-tools')`.
  // --------------------------------------------------------------------------
  function ensureToolsRow(brand) {
    let row = document.getElementById('dd-brand-tools');
    if (row && brand.contains(row)) return row;
    row?.remove();
    row = document.createElement('div');
    row.id = 'dd-brand-tools';
    row.className = 'dd-brand-tools';
    // The brand itself is a button (logo → Compliance profile, TASK-1201):
    // stop every event the brand listens to so a tool never ALSO navigates.
    row.addEventListener('click', (e) => { e.stopPropagation(); });
    row.addEventListener('keydown', (e) => { e.stopPropagation(); });
    const logo = brand.querySelector('.sidebar-logo');
    if (logo && logo.nextSibling) brand.insertBefore(row, logo.nextSibling); else brand.appendChild(row);
    return row;
  }
  function ensureOrb() {
    let orb = document.getElementById('dd-notes-orb');
    const brand = document.querySelector('#app-sidebar .sidebar-brand');
    if (!brand) return null;
    const row = ensureToolsRow(brand);
    if (orb && row.contains(orb)) return orb;
    orb?.remove();
    orb = document.createElement('button');
    orb.type = 'button';
    orb.id = 'dd-notes-orb';
    orb.className = 'dd-notes-orb';
    orb.setAttribute('data-trailguide', 'notes');
    orb.setAttribute('aria-label', 'Notes');
    orb.title = 'Notes';
    orb.innerHTML = '<span class="material-icons" aria-hidden="true">sticky_note_2</span><span class="dd-notes-orb-n" id="dd-notes-orb-n" hidden>0</span>';
    orb.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); hidePeek(); open(); });
    orb.addEventListener('mouseenter', showPeek);
    orb.addEventListener('mouseleave', hidePeek);
    orb.addEventListener('focus', showPeek);
    orb.addEventListener('blur', hidePeek);
    row.insertBefore(orb, row.firstChild);
    return orb;
  }

  function renderOrb() {
    const orb = document.getElementById('dd-notes-orb');
    if (!orb) return;
    const n = S.key && canUse() ? unreadThreads().length : 0;
    orb.style.display = S.key && canUse() ? '' : 'none';
    orb.classList.toggle('has-unread', n > 0);
    const badge = document.getElementById('dd-notes-orb-n');
    if (badge) { badge.hidden = n === 0; badge.textContent = n > 99 ? '99+' : String(n); }
    orb.setAttribute('aria-label', n ? `Notes, ${n} unread` : 'Notes');
  }

  // Hover peek: the newest UNREAD note (or the newest note). position:fixed —
  // the sidebar scrolls (overflow-y:auto), so an absolutely-positioned bubble
  // would be clipped at its edge. 350ms delay so a passing mouse never flashes
  // someone's note while screen-sharing.
  let _peekTimer = null;
  function showPeek() {
    clearTimeout(_peekTimer);
    _peekTimer = setTimeout(() => {
      const orb = document.getElementById('dd-notes-orb');
      if (!orb || isPanelOpen()) return;
      const t = unreadThreads()[0] || S.threads.filter((x) => !isArchived(x))[0] || null;
      let el = document.getElementById('dd-notes-peek');
      if (!el) {
        el = document.createElement('div');
        el.id = 'dd-notes-peek';
        el.className = 'dd-notes-peek';
        el.setAttribute('aria-hidden', 'true');
        document.body.appendChild(el);
      }
      const n = unreadThreads().length;
      if (!t) {
        el.innerHTML = `<span class="pk-h">Notes</span><span class="pk-b pk-empty">No notes yet. Click to write one — pick who gets it on the To: line.</span>`;
      } else {
        el.innerHTML = `<span class="pk-h">${n ? 'Newest unread' : 'Latest'} · ${esc(nameOf(t, t.lastMessageUid))} · ${esc(when(t.lastMessageAtMs))}</span>`
          + `<span class="pk-b">${esc(t.preview)}</span>`
          + `<span class="pk-f">${n ? n + ' unread · ' : ''}click to open</span>`;
      }
      const r = orb.getBoundingClientRect();
      el.style.top = Math.round(r.bottom + 10) + 'px';
      el.style.left = Math.max(8, Math.min(window.innerWidth - 316, Math.round(r.left - 120))) + 'px';
      el.classList.add('on');
    }, 350);
  }
  function hidePeek() {
    clearTimeout(_peekTimer);
    document.getElementById('dd-notes-peek')?.classList.remove('on');
  }

  // Phone: the More-sheet row + a dot on the More button.
  function showEntry(show) {
    const btn = document.getElementById('mobile-notes-btn');
    if (btn) btn.classList.toggle('hidden', !show);
    renderNavBadge();
  }
  function renderNavBadge() {
    const n = S.key && canUse() ? unreadThreads().length : 0;
    const btn = document.getElementById('mobile-notes-btn');
    if (btn) {
      let b = btn.querySelector('.dd-notes-nav-badge');
      if (!n) b?.remove();
      else {
        if (!b) { b = document.createElement('span'); b.className = 'dd-notes-nav-badge'; btn.appendChild(b); }
        b.textContent = n > 9 ? '9+' : String(n);
      }
    }
    const more = document.getElementById('mobile-nav-more');
    if (more) {
      let dot = more.querySelector('.dd-notes-more-dot');
      if (!n) dot?.remove();
      else if (!dot) { dot = document.createElement('span'); dot.className = 'dd-notes-more-dot'; dot.setAttribute('aria-hidden', 'true'); more.appendChild(dot); }
    }
  }

  function render() {
    renderOrb();
    renderNavBadge();
    if (isPanelOpen()) renderPanel();
  }

  // --------------------------------------------------------------------------
  // Panel (side panel on desktop, full screen on phones)
  // --------------------------------------------------------------------------
  function isPanelOpen() {
    const m = document.getElementById('dd-notes-modal');
    return !!(m && !m.classList.contains('hidden'));
  }

  function ensurePanel() {
    let m = document.getElementById('dd-notes-modal');
    if (m) return m;
    m = document.createElement('div');
    m.id = 'dd-notes-modal';
    m.className = 'modal hidden';
    m.setAttribute('role', 'dialog');
    m.setAttribute('aria-modal', 'true');
    m.setAttribute('aria-labelledby', 'dd-notes-title');
    m.innerHTML = `<div class="modal-content modal-fullscreen dd-notes-content">
      <div class="modal-header dd-notes-head">
        <h3 id="dd-notes-title"><span class="material-icons" aria-hidden="true">sticky_note_2</span>Notes</h3>
        <div class="dd-notes-head-actions">
          <button type="button" class="btn btn-primary btn-sm" data-tn="new"><span class="material-icons">add</span>New note</button>
          <button type="button" class="btn-icon" data-tn="close" aria-label="Close"><span class="material-icons">close</span></button>
        </div>
      </div>
      <div class="dd-notes-body" id="dd-notes-body"></div>
    </div>
    <div class="dd-pick hidden" id="dd-notes-pick" role="listbox" aria-label="Pick who gets this note"></div>`;
    document.body.appendChild(m);
    m.addEventListener('click', onPanelClick);
    m.addEventListener('keydown', onPanelKey);
    m.addEventListener('input', onPanelInput);
    // Picking a name must not blur the textarea first (on a phone the blur
    // drops the keyboard, the layout jumps and the tap lands somewhere else).
    const keepFocus = (e) => { if (e.target && e.target.closest && e.target.closest('[data-tn-pick]')) e.preventDefault(); };
    m.addEventListener('pointerdown', keepFocus);
    m.addEventListener('mousedown', keepFocus);
    // The picker is position:fixed, so it follows its textarea on scroll/resize.
    m.addEventListener('scroll', repositionPick, true);
    window.addEventListener('resize', repositionPick);
    try { window.visualViewport?.addEventListener('resize', repositionPick); } catch (e) { /* ignore */ }
    return m;
  }

  function open() {
    start();
    if (!canUse()) { toast('Notes is not turned on for your login.', 'error'); return; }
    loadSendFeedback().catch(() => {}); // TASK-10112 — ready before Send is pressed
    const m = ensurePanel();
    // Reopen where you left off: a half-written new note or a thread reply
    // (it used to force the list, which dropped the new-note draft).
    if (S.view === 'thread' && !find(S.open)) S.view = 'list';
    if (S.view !== 'thread') S.open = null;
    if (S.view !== 'thread' && S.view !== 'compose') S.view = 'list';
    loadMentions(ctxOf());
    renderPanel();
    m.classList.remove('hidden');
  }
  function openThread(id) {
    start();
    if (!canUse()) return;
    const m = ensurePanel();
    if (isPanelOpen()) captureComposer();
    hidePick();
    S.view = 'thread';
    S.open = id;
    S.editing = null;
    loadMentions(id);
    persist();
    renderPanel();
    m.classList.remove('hidden');
  }
  function close() {
    const m = document.getElementById('dd-notes-modal');
    if (m && !m.classList.contains('hidden')) captureComposer();
    hidePick();
    if (m) m.classList.add('hidden');
    S.editing = null;
  }

  function renderPanel() {
    const body = document.getElementById('dd-notes-body');
    if (!body) return;
    // Every snapshot re-renders the panel; a half-typed reply, new note or
    // edit must survive it (COD Code Reviewer). Textareas are keyed by id.
    const drafts = snapshotDrafts();
    if (S.view === 'thread') {
      const t = find(S.open);
      if (!t) { S.view = 'list'; S.open = null; }
      else { renderThread(body, t); fillFromDraft(); restoreDrafts(drafts); afterComposerRender(); return; }
    }
    if (S.view === 'compose') { renderCompose(body); fillFromDraft(); restoreDrafts(drafts); afterComposerRender(); return; }
    hidePick();
    renderList(body);
  }
  // Each textarea carries data-ctx (the thread id, the message being edited,
  // or 'compose'); a draft only comes back into a box with the SAME ctx, so
  // jumping straight from one thread to another never carries text across,
  // and an emptied box stays empty (delta COD: `if (k.value)` snapped an
  // emptied edit back to the saved message).
  function snapshotDrafts() {
    const out = {};
    document.querySelectorAll('#dd-notes-modal .dd-ta').forEach((ta) => {
      out[ta.id] = { ctx: ta.dataset.ctx || '', value: ta.value, s: ta.selectionStart, e: ta.selectionEnd, focused: document.activeElement === ta };
    });
    return out;
  }
  /** Put the saved draft for the composer in view back into its box. */
  function fillFromDraft() {
    const ctx = ctxOf();
    const ta = activeTextarea();
    const d = ctx && S.drafts[ctx];
    if (ta && d && (ta.dataset.ctx || '') === ctx) ta.value = d.text;
  }
  function restoreDrafts(drafts) {
    for (const [id, k] of Object.entries(drafts)) {
      const ta = document.getElementById(id);
      if (!ta || (ta.dataset.ctx || '') !== k.ctx) continue;
      ta.value = k.value;
      if (k.focused) { ta.focus(); try { ta.setSelectionRange(k.s, k.e); } catch (e) { /* ignore */ } }
    }
  }

  function renderList(body) {
    const items = visibleThreads();
    const tabs = TABS.map(([k, label]) => {
      const n = k === 'forme' ? unreadThreads().filter((t) => t.authorUid !== me()).length : 0;
      return `<button type="button" class="dd-tab${S.tab === k ? ' on' : ''}" data-tn="tab" data-tab="${k}">${label}${n ? `<span class="dd-tab-n">${n}</span>` : ''}</button>`;
    }).join('');
    const rows = items.map((t) => {
      const who = others(t);
      const line = whoLine(t);
      const by = t.lastMessageUid === me() ? 'You: ' : (t.participantUids.length > 2 ? esc(nameOf(t, t.lastMessageUid)) + ': ' : '');
      const initial = t.audience === 'everyone' ? 'E' : (who[0] || 'Y').trim().charAt(0).toUpperCase();
      return `<button type="button" class="dd-row${isUnread(t) ? ' unread' : ''}${isArchived(t) ? ' archived' : ''}" data-tn="open" data-id="${esc(t.id)}">
        <span class="dd-row-av" aria-hidden="true">${esc(initial)}</span>
        <span class="dd-row-main">
          <span class="dd-row-top"><span class="dd-row-who">${esc(line)}</span><span class="dd-row-when">${esc(when(t.lastMessageAtMs))}</span></span>
          <span class="dd-row-prev">${by}${esc(t.preview)}</span>
        </span>
        ${isUnread(t) ? '<span class="dd-row-dot" aria-label="unread"></span>' : ''}
      </button>`;
    }).join('');
    const empty = S.tab === 'forme'
      ? 'Nothing shared with you yet. When a teammate mentions you, the note lands here.'
      : (S.tab === 'sent' ? 'You haven\'t written a note yet.' : 'No notes yet.');
    const cd = S.drafts.compose;
    const draftRow = cd && cd.text.trim() ? `<button type="button" class="dd-row dd-draft-row" data-tn="new">
        <span class="dd-row-av" aria-hidden="true"><span class="material-icons" style="font-size:18px">edit_note</span></span>
        <span class="dd-row-main"><span class="dd-row-top"><span class="dd-row-who">Draft</span><span class="dd-row-when">${esc(when(cd.at))}</span></span>
        <span class="dd-row-prev">${esc(cd.text)}</span></span></button>` : '';
    body.innerHTML = `<div class="dd-tabs">${tabs}${S.tab === 'all' ? `<label class="dd-arch-toggle"><input type="checkbox" data-tn="archived"${S.showArchived ? ' checked' : ''}> Show archived</label>` : ''}</div>
      <div class="dd-list">${draftRow}${rows || `<div class="dd-empty">${empty}<br><span class="dd-empty-sub">Tap <strong>New note</strong> to write one, then pick who gets it on the <strong>To:</strong> line — they see the same note, not a copy.</span></div>`}</div>`;
  }

  // The people a thread already reaches (chips that can't be removed from a
  // reply) — one "Everyone (n)" chip for an @everyone thread.
  function fixedChips() {
    if (S.view !== 'thread') return [];
    const t = find(S.open);
    if (!t) return [];
    if (t.audience === 'everyone') return [`${EVERYONE_NAME} (${t.participantUids.length})`];
    return others(t);
  }
  // The To: line — NEVER blank: "Just me (private)" until someone is picked,
  // then the people it reaches as blue chips. Tapping "+ Add person" opens the
  // picker (the PRIMARY way to pick; `@` in the text is the shortcut).
  function toLineHtml(id) {
    const fixed = fixedChips().map((n) => `<span class="dd-chip fixed">${esc(n)}</span>`).join('');
    const picked = [...S.mentions.entries()].map(([uid, name]) => `<span class="dd-chip${uid === EVERYONE ? ' everyone' : ''}">${esc(name)}<button type="button" class="dd-chip-x" data-tn="unmention" data-uid="${esc(uid)}" aria-label="Remove ${esc(name)}">&times;</button></span>`).join('');
    const none = !fixed && !picked;
    const add = S.pickable.length ? `<button type="button" class="dd-to-add" id="${id}-add" data-tn="to-add" aria-haspopup="listbox"><span class="material-icons" aria-hidden="true">person_add</span>${none ? 'Add person' : 'Add'}</button>` : '';
    return `<div class="dd-to" data-tn-to><span class="dd-to-label">To:</span>${fixed}${picked}${none ? '<span class="dd-to-me">Just me (private)</span>' : ''}${add}</div>`;
  }

  function composerHtml(id, ctx, placeholder, sendLabel) {
    const isReply = ctx !== 'compose';
    let hint;
    if (S.pickable.length) {
      hint = isReply
        ? 'Replies go to everyone on this note. Type <strong>@</strong> or tap <strong>Add</strong> to bring someone else in.'
        : 'Tap <strong>Add person</strong> or type <strong>@</strong> and pick a name — only the people you pick can read it. Nobody is texted unless you tick <strong>Also text it</strong>.';
    } else {
      hint = isDemo() ? 'In the demo there is nobody else to send to — on your account, pick a teammate on the To: line.'
        : 'Nobody else on your account has Notes turned on yet (Roster → staff login → tick Notes). Until then a note is just for you.';
    }
    return `<div class="dd-composer" id="${id}">
      ${toLineHtml(id)}
      <div class="dd-hint" id="${id}-hint">${hint}</div>
      <div class="dd-ta-wrap">
        <textarea class="dd-ta" id="${id}-ta" data-ctx="${esc(ctx)}" maxlength="${TEXT_MAX}" rows="3" placeholder="${esc(placeholder)}" autocomplete="off"></textarea>
      </div>
      <div class="dd-composer-foot">
        <span class="dd-foot-note">Defects still go in Inspections / Maintenance.</span>
        <label class="dd-also" id="${id}-also" hidden title="Also send this note as a text to the people on it who can get texts"><input type="checkbox" data-tn="also-text"> Also text it</label>
        <button type="button" class="btn btn-primary btn-sm" data-tn="send"><span class="material-icons">send</span>${esc(sendLabel)}</button>
      </div>
      <div class="dd-also-who" id="${id}-also-who" hidden></div>
      <div class="dd-send-status" id="${id}-status" hidden></div>
      <div class="dd-err" id="${id}-err" role="alert"></div>
    </div>`;
  }

  // TASK-10142 (left-open 4) — a note reaches logins on Notes only; a memo
  // reaches roster people (drivers too) by text or email.
  // The New note screen links to HR → Driver Memos → New Memo for anyone who
  // can send one: mirror of hr.js canSendMemos (owner, or an active, not
  // read-only login holding 'hr'; demo yes) — affordance only, the memo
  // callable enforces. Compliance-only companies get no memos, so no link.
  // The navigation lives in the lazy utils/notesMemoLink.js (perf budget).
  function canSendMemo() {
    const st = window.AppState || {};
    const uid = st.user?.uid;
    const ma = st.memberAccess;
    if (isDriverContext() || st.tenantData?.complianceOnly === true) return false;
    if (st.isDemo || st.tenant === 'demo') return true; // hr.js's demo test, exactly
    if (!uid || !tenantId() || st.user.isAnonymous) return false;
    if (st.tenantData?.ownerId === uid) return true;
    return !!(ma && Array.isArray(ma.modules) && ma.modules.includes('hr') && !INACTIVE.includes(ma.status) && ma.readOnly !== true);
  }
  function memoPointerHtml() {
    return canSendMemo() ? '<div class="dd-memo-ptr"><span>For drivers or the whole crew, by text or email:</span><button type="button" class="dd-link" data-tn="memo">Send a memo (text or email)</button></div>' : '';
  }
  function openMemo() {
    captureComposer(); // the half-written note waits in Notes
    close();
    const tenant = window.AppState?.tenant || null; // a company switch before HR opens cancels it
    __ddChunkWrap(import('./utils/notesMemoLink.js?v=10142004')).then(() => window.NotesMemoLink.open({ tenant }))
      .catch(() => toast('Open HR → Driver Memos → New Memo to send a memo.', 'info'));
  }

  function renderCompose(body) {
    body.innerHTML = `<div class="dd-sub"><button type="button" class="dd-back" data-tn="back"><span class="material-icons">arrow_back</span>Notes</button><span class="dd-sub-title">New note</span></div>
      <div class="dd-scroll dd-compose-wrap">${composerHtml('dd-c', 'compose', 'Write a note…', 'Send')}${memoPointerHtml()}</div>`;
    setTimeout(() => document.getElementById('dd-c-ta')?.focus(), 30);
  }

  function renderThread(body, t) {
    const mine = me();
    const msgs = t.messages.map((m) => {
      const own = m.uid === mine;
      const canEdit = own && (Date.now() - Number(m.atMs || 0)) < EDIT_WINDOW_MS;
      if (S.editing === m.id) {
        return `<div class="dd-msg mine editing" data-mid="${esc(m.id)}">
          <textarea class="dd-ta" id="dd-edit-ta" data-ctx="${esc(m.id)}" maxlength="${TEXT_MAX}" rows="3">${esc(m.text)}</textarea>
          <div class="dd-msg-actions"><button type="button" class="btn btn-secondary btn-sm" data-tn="edit-cancel">Cancel</button><button type="button" class="btn btn-primary btn-sm" data-tn="edit-save" data-mid="${esc(m.id)}">Save</button></div>
        </div>`;
      }
      return `<div class="dd-msg${own ? ' mine' : ''}" data-mid="${esc(m.id)}">
        <div class="dd-msg-meta"><strong>${esc(own ? 'You' : (m.name || nameOf(t, m.uid)))}</strong> · ${esc(whenLong(m.atMs))}${m.editedAtMs ? ' · edited' : ''}${canEdit ? ` <button type="button" class="dd-link" data-tn="edit" data-mid="${esc(m.id)}">Edit</button>` : ''}</div>
        <div class="dd-msg-text">${renderText(t, m.text)}</div>
      </div>`;
    }).join('');
    const archived = isArchived(t);
    body.innerHTML = `<div class="dd-sub">
        <button type="button" class="dd-back" data-tn="back"><span class="material-icons">arrow_back</span>Notes</button>
        <span class="dd-sub-title">${esc(whoLine(t))}</span>
        <button type="button" class="btn btn-secondary btn-sm dd-arch" data-tn="${archived ? 'unarchive' : 'archive'}" title="${archived ? 'Put it back in your list' : 'Hide it from your list (others keep it)'}"><span class="material-icons">${archived ? 'unarchive' : 'archive'}</span>${archived ? 'Unarchive' : 'Archive for me'}</button>
      </div>
      <div class="dd-scroll dd-msgs" id="dd-msgs">${msgs}</div>
      ${composerHtml('dd-r', t.id, 'Reply…', 'Reply')}`;
    const box = document.getElementById('dd-msgs');
    if (box) box.scrollTop = box.scrollHeight;
    if (isUnread(t)) markRead(t);
  }

  async function markRead(t) {
    // Optimistic: clear the badge now; the snapshot confirms it.
    t.readAtMs = { ...(t.readAtMs || {}), [me()]: t.lastMessageAtMs };
    renderOrb();
    renderNavBadge(); // no panel re-render here: keep the thread's scroll position
    try { await call({ op: 'markRead', id: t.id }); } catch (e) { console.warn('[TeamNotes] markRead failed:', e?.code || e?.message || e); }
  }

  // --------------------------------------------------------------------------
  // Events
  // --------------------------------------------------------------------------
  function onPanelClick(ev) {
    const m = document.getElementById('dd-notes-modal');
    if (ev.target === m) { close(); return; }
    const pick = ev.target.closest('[data-tn-pick]');
    if (pick) { ev.preventDefault(); choose(pick.getAttribute('data-tn-pick')); return; }
    // A click anywhere outside the open picker (and off the To: button) closes it.
    const pk = pickEl();
    if (pk && !pk.classList.contains('hidden') && !ev.target.closest('.dd-pick') && !ev.target.closest('[data-tn="to-add"]')) hidePick();
    const el = ev.target.closest('[data-tn]');
    if (!el) return;
    const k = el.getAttribute('data-tn');
    if (k === 'close') { close(); return; }
    if (k === 'to-add') { ev.preventDefault(); openToPicker(el); return; }
    if (k === 'new') { captureComposer(); S.view = 'compose'; S.open = null; S.editing = null; loadMentions('compose'); persist(); renderPanel(); return; }
    if (k === 'back') { captureComposer(); S.view = 'list'; S.open = null; S.editing = null; S.mentions.clear(); persist(); renderPanel(); return; }
    if (k === 'tab') { S.tab = el.getAttribute('data-tab') || 'forme'; renderPanel(); return; }
    if (k === 'archived') { S.showArchived = !!el.checked; renderPanel(); return; }
    if (k === 'open') { openThread(el.getAttribute('data-id')); return; }
    if (k === 'unmention') {
      const uid = el.getAttribute('data-uid');
      // The chip is the recipient; the `@Name` in the text would now be a lie
      // (and the typo guard would block Send on it) — take it out too.
      stripMention(activeTextarea(), S.mentions.get(uid));
      S.mentions.delete(uid);
      refreshChips();
      captureComposer();
      return;
    }
    if (k === 'also-text') { S.tp.checked = !!el.checked; paintTextPreview(); if (S.tp.checked) scheduleTextPreview(0); return; }
    if (k === 'send') { send(); return; }
    if (k === 'memo') { openMemo(); return; }
    if (k === 'archive' || k === 'unarchive') { archive(k === 'archive'); return; }
    if (k === 'edit') { S.editing = el.getAttribute('data-mid'); renderPanel(); setTimeout(() => document.getElementById('dd-edit-ta')?.focus(), 30); return; }
    if (k === 'edit-cancel') { S.editing = null; renderPanel(); return; }
    if (k === 'edit-save') { saveEdit(el.getAttribute('data-mid')); return; }
  }

  function onPanelKey(ev) {
    if (ev.key === 'Escape') {
      const pickOpen = document.querySelector('#dd-notes-modal .dd-pick:not(.hidden)');
      if (pickOpen) { hidePick(); ev.stopPropagation(); return; }
      close();
      return;
    }
    const q = ev.target.closest('.dd-pick-q');
    const ta = q ? document.getElementById(pickEl()?.dataset.for || '') : ev.target.closest('.dd-ta');
    if (!ta) return;
    const pick = pickFor(ta);
    const open = !!pick;
    if (open && (ev.key === 'ArrowDown' || ev.key === 'ArrowUp')) {
      ev.preventDefault();
      const n = pick.querySelectorAll('[data-tn-pick]').length;
      S.pickIdx = (S.pickIdx + (ev.key === 'ArrowDown' ? 1 : n - 1)) % Math.max(n, 1);
      paintPick(pick);
      return;
    }
    if (open && (ev.key === 'Enter' || ev.key === 'Tab')) {
      const cur = pick.querySelectorAll('[data-tn-pick]')[S.pickIdx];
      if (cur) { ev.preventDefault(); choose(cur.getAttribute('data-tn-pick')); }
      return;
    }
    if (!q && ev.key === 'Enter' && (ev.metaKey || ev.ctrlKey) && ta.id !== 'dd-edit-ta') { ev.preventDefault(); send(); }
  }

  function onPanelInput(ev) {
    const q = ev.target.closest('.dd-pick-q');
    if (q) { paintToList(q.value); return; }
    const ta = ev.target.closest('.dd-ta');
    if (!ta || ta.id === 'dd-edit-ta') return;
    const err = document.getElementById((S.view === 'thread' ? 'dd-r' : 'dd-c') + '-err');
    if (err && err.textContent) err.textContent = '';
    updatePick(ta);
    captureComposer();
    if (S.tp.checked) scheduleTextPreview(900);   // TASK-9835 D — the size / credits follow the text
  }

  // `@` handling: the token under the caret (`@lac`) filters the pickable list.
  function mentionToken(ta) {
    const upto = ta.value.slice(0, ta.selectionStart);
    const m = upto.match(/(^|\s)@([^\s@]{0,40})$/);
    return m ? { q: m[2], start: upto.length - m[2].length - 1 } : null;
  }
  // The picker used to sit inside the composer and open UPWARD; in the New-note
  // view that is inside .dd-scroll (overflow:auto), so it was clipped and every
  // click/tap landed on the modal header (verify lane 2026-10-12). It now lives
  // at the overlay root with position:fixed, opens below the textarea when
  // there is room (else above), and is clamped to the visible viewport.
  function pickEl() { return document.getElementById('dd-notes-pick'); }
  /** The open picker, if it belongs to this textarea. */
  function pickFor(ta) {
    const pick = pickEl();
    return pick && !pick.classList.contains('hidden') && pick.dataset.for === ta.id ? pick : null;
  }
  function hidePick() {
    const pick = pickEl();
    if (pick) { pick.classList.add('hidden'); pick.dataset.for = ''; pick.dataset.mode = ''; pick.dataset.anchor = ''; }
  }
  /** The element the picker hangs off: the To: button in `to` mode, else its textarea. */
  function pickAnchor(pick, ta) {
    return (pick.dataset.mode === 'to' && document.getElementById(pick.dataset.anchor || '')) || ta;
  }
  /** Everyone first, then teammates — minus the ones already picked. */
  function candidates(q) {
    const s = String(q || '').trim().toLowerCase();
    const out = [];
    const th = S.view === 'thread' ? find(S.open) : null;
    const mayEveryone = !th || (th.authorUid === me() && th.audience !== 'everyone');
    if (S.pickable.length && mayEveryone && !S.mentions.has(EVERYONE) && (!s || EVERYONE_NAME.toLowerCase().startsWith(s))) out.push({ uid: EVERYONE, name: EVERYONE_NAME, everyone: true });
    for (const p of S.pickable) if (!S.mentions.has(p.uid) && (!s || p.name.toLowerCase().includes(s))) out.push(p);
    return out;
  }
  function itemsHtml(hits) {
    return hits.map((p) => `<button type="button" class="dd-pick-item${p.everyone ? ' everyone' : ''}" role="option" data-tn-pick="${esc(p.uid)}"><span class="dd-row-av" aria-hidden="true">${p.everyone ? '<span class="material-icons" style="font-size:16px">groups</span>' : esc(p.name.charAt(0).toUpperCase())}</span>${esc(p.name)}${p.everyone ? `<span class="dd-pick-sub">all ${S.pickable.length + 1} people on Notes</span>` : ''}</button>`).join('');
  }
  // "+ Add person" on the To: line — the picker opens under that button with a
  // name filter; a pick becomes a chip WITHOUT touching the text.
  function openToPicker(btn) {
    const pick = pickEl();
    const ta = activeTextarea();
    if (!pick || !ta || !btn) return;
    if (!pick.classList.contains('hidden') && pick.dataset.mode === 'to') { hidePick(); return; }
    pick.dataset.for = ta.id;
    pick.dataset.mode = 'to';
    pick.dataset.anchor = btn.id;
    pick.innerHTML = `<input type="text" class="dd-pick-q" placeholder="Type a name" aria-label="Find a teammate" autocomplete="off"><div class="dd-pick-list"></div>`;
    paintToList('');
    pick.classList.remove('hidden');
    placePick(pick, btn);
    setTimeout(() => pick.querySelector('.dd-pick-q')?.focus(), 20);
  }
  function paintToList(q) {
    const pick = pickEl();
    const list = pick && pick.querySelector('.dd-pick-list');
    if (!list) return;
    const hits = candidates(q);
    S.pickIdx = 0;
    list.innerHTML = hits.length ? itemsHtml(hits) : `<div class="dd-pick-empty">${S.pickable.length ? 'No teammate matches.' : 'Nobody else has Notes turned on yet.'}</div>`;
    paintPick(pick);
    const ta = document.getElementById(pick.dataset.for || '');
    if (ta) placePick(pick, pickAnchor(pick, ta));
  }
  function placePick(pick, ta) {
    const r = ta.getBoundingClientRect();
    const vv = window.visualViewport;
    const topLim = (vv ? vv.offsetTop : 0) + 8;
    const botLim = (vv ? vv.offsetTop + vv.height : window.innerHeight) - 8;
    const vw = window.innerWidth;
    pick.style.maxHeight = '';
    const want = Math.min(pick.scrollHeight + 2, 240);
    const below = botLim - r.bottom - 4;
    const above = r.top - 4 - topLim;
    const down = below >= want || below >= above;
    const room = Math.max(60, Math.min(240, down ? below : above));
    const h = Math.min(want, room);
    pick.style.maxHeight = room + 'px';
    pick.style.top = Math.round(down ? r.bottom + 4 : r.top - 4 - h) + 'px';
    const w = pick.offsetWidth || 240;
    pick.style.left = Math.round(Math.max(8, Math.min(vw - w - 8, r.left + 8))) + 'px';
  }
  function repositionPick() {
    const pick = pickEl();
    if (!pick || pick.classList.contains('hidden')) return;
    const ta = document.getElementById(pick.dataset.for || '');
    if (!ta || !isPanelOpen()) { hidePick(); return; }
    placePick(pick, pickAnchor(pick, ta));
  }
  // `@` in the text: the token under the caret filters the same list (text mode).
  function updatePick(ta) {
    const pick = pickEl();
    if (!pick) return;
    const tok = mentionToken(ta);
    if (!tok) { if (pick.dataset.for === ta.id && pick.dataset.mode !== 'to') hidePick(); return; }
    pick.dataset.for = ta.id;
    pick.dataset.mode = 'text';
    pick.dataset.anchor = '';
    const hits = candidates(tok.q).slice(0, 8);
    if (!hits.length) {
      pick.innerHTML = `<div class="dd-pick-empty">${S.pickable.length ? 'No teammate matches.' : 'Nobody else has Notes turned on yet.'}</div>`;
      pick.classList.remove('hidden');
      placePick(pick, ta);
      return;
    }
    S.pickIdx = 0;
    pick.innerHTML = itemsHtml(hits);
    paintPick(pick);
    pick.classList.remove('hidden');
    placePick(pick, ta);
  }
  function paintPick(pick) {
    pick.querySelectorAll('[data-tn-pick]').forEach((el, i) => el.classList.toggle('on', i === S.pickIdx));
  }
  function activeTextarea() {
    return document.getElementById(S.view === 'thread' ? 'dd-r-ta' : 'dd-c-ta');
  }
  function choose(uid) {
    const p = uid === EVERYONE ? { uid: EVERYONE, name: EVERYONE_NAME } : S.pickable.find((x) => x.uid === uid);
    const ta = activeTextarea();
    if (!p || !ta) return;
    const pick = pickEl();
    const fromTo = !!(pick && pick.dataset.mode === 'to');
    const tok = mentionToken(ta);
    if (!fromTo || tok) {
      // Typed `@lac` → replace the token with the full @Name in the text.
      const before = tok ? ta.value.slice(0, tok.start) : ta.value.slice(0, ta.selectionStart);
      const after = ta.value.slice(ta.selectionStart);
      const ins = '@' + p.name + ' ';
      ta.value = before + ins + after;
      const pos = (before + ins).length;
      ta.setSelectionRange(pos, pos);
    }
    S.mentions.set(p.uid, p.name);
    refreshChips();
    hidePick();
    ta.focus();
    captureComposer();
  }
  function refreshChips() {
    const to = document.querySelector('#dd-notes-modal [data-tn-to]');
    if (!to) return;
    const id = S.view === 'thread' ? 'dd-r' : 'dd-c';
    to.outerHTML = toLineHtml(id);
    scheduleTextPreview(); // TASK-9835 D — who could get a text depends on who it goes to
  }

  // --------------------------------------------------------------------------
  // TASK-9835 D — "Also text it" + TASK-10112 send feedback.
  // The checkbox sits next to Send, OFF by default, and only shows when the
  // SERVER says at least one person on the note can get a text (phone + SMS
  // opt-in). Ticking it shows who will and won't get the text and the credits
  // (window.SendFeedback preview). The server re-checks everything at send.
  // --------------------------------------------------------------------------
  let _sfLoad = null;
  function loadSendFeedback() {
    if (window.SendFeedback) return Promise.resolve(window.SendFeedback);
    if (!_sfLoad) {
      // The "Also text it" styles ride in the lazy chunk (cold-load budget).
      _sfLoad = __ddChunkWrap(import('./utils/sendFeedback.js?v=10112001')).then(() => { window.SendFeedback.ensureNotesCss(); return window.SendFeedback; })
        .catch((e) => { _sfLoad = null; throw e; });
    }
    return _sfLoad;
  }
  function audienceKey() {
    const ctx = ctxOf();
    if (!ctx) return null;
    const picks = [...S.mentions.keys()].sort().join(',');
    return `${ctx}|${picks}`;
  }
  function scheduleTextPreview(delay = 350) {
    if (S.tp.timer) clearTimeout(S.tp.timer);
    S.tp.timer = setTimeout(() => { S.tp.timer = null; refreshTextPreview(); }, delay);
  }
  async function refreshTextPreview() {
    const key = audienceKey();
    if (!key || isDemo()) { S.tp = { ...S.tp, key, data: null, checked: false }; paintTextPreview(); return; }
    const isReply = S.view === 'thread';
    const everyone = S.mentions.has(EVERYONE);
    const mentionUids = [...S.mentions.keys()].filter((u) => u !== EVERYONE);
    if (!isReply && !everyone && !mentionUids.length) { S.tp = { ...S.tp, key, data: null, checked: false }; paintTextPreview(); return; }
    if (S.tp.key !== key) S.tp.checked = false;   // a new audience starts unticked
    const seq = ++S.tp.seq;
    const text = String(activeTextarea()?.value || '').trim();
    try {
      const data = await call({ op: 'textPreview', ...(isReply ? { id: S.open } : {}), mentionUids, everyone, text });
      if (seq !== S.tp.seq || audienceKey() !== key) return;   // a newer ask is in flight
      S.tp.key = key; S.tp.data = data;
      if (!(data && Array.isArray(data.canText) && data.canText.length)) S.tp.checked = false;
      await loadSendFeedback().catch(() => null);
      paintTextPreview();
    } catch (e) {
      if (seq !== S.tp.seq) return;
      S.tp.key = key; S.tp.data = null; S.tp.checked = false;   // no preview -> no checkbox (never a blind text)
      paintTextPreview();
    }
  }
  function paintTextPreview() {
    const id = S.view === 'thread' ? 'dd-r' : 'dd-c';
    const label = document.getElementById(`${id}-also`);
    const who = document.getElementById(`${id}-also-who`);
    if (!label || !who) return;
    const d = S.tp.key === audienceKey() ? S.tp.data : null;
    const can = d && Array.isArray(d.canText) ? d.canText : [];
    label.hidden = !can.length;
    const box = label.querySelector('input');
    if (box) box.checked = !!(can.length && S.tp.checked);
    if (!can.length || !S.tp.checked || !window.SendFeedback) { who.hidden = true; who.innerHTML = ''; return; }
    who.innerHTML = window.SendFeedback.noteTextWhoHtml(d, String(activeTextarea()?.value || '').trim().length, Date.now(), window.AppState?.tenantData); // + quiet-hours warning (Chris 10/26)
    who.hidden = false;
  }
  // Every snapshot re-renders the composer: paint the cached answer straight
  // back (no flicker, no extra call), ask again only when the audience moved.
  function afterComposerRender() {
    if (S.busy) {
      const b = document.querySelector('#dd-notes-modal [data-tn="send"]');
      if (b) b.disabled = true;   // a send in flight keeps the (re-rendered) button locked
    }
    paintTextPreview();
    if (S.tp.key !== audienceKey()) scheduleTextPreview(0);
  }
  function wantsText() {
    const id = S.view === 'thread' ? 'dd-r' : 'dd-c';
    const label = document.getElementById(`${id}-also`);
    const box = label && label.querySelector('input');
    return !!(label && !label.hidden && box && box.checked && S.tp.key === audienceKey() && S.tp.data);
  }

  // --------------------------------------------------------------------------
  // Actions
  // --------------------------------------------------------------------------
  async function send() {
    if (S.busy) return;
    const isReply = S.view === 'thread';
    const ta = activeTextarea();
    const err = document.getElementById(isReply ? 'dd-r-err' : 'dd-c-err');
    if (!ta) return;
    if (err) err.textContent = '';
    const text = String(ta.value || '').trim();
    if (!text) { if (err) err.textContent = 'Write something first.'; return; }
    // Typo guard: an `@word` in the text that never became a chip would send a
    // private note to yourself with no warning (slice 1: recipients = chips
    // only). Block and say so. Names already on the thread count as covered.
    const th = isReply ? find(S.open) : null;
    const covered = [...S.mentions.values(), ...(th ? others(th) : []), ...(th && th.audience === 'everyone' ? [EVERYONE_NAME] : [])];
    const missing = unmatchedMentions(text, covered);
    if (missing.length) {
      const w = missing[0];
      if (err) {
        err.textContent = /^everyone$/i.test(w) ? 'To send to everyone, pick Everyone from the list.'
          : `Nobody named '${w}' — pick from the list, or remove the @.`;
      }
      return;
    }
    const everyone = S.mentions.has(EVERYONE);
    const mentionUids = [...S.mentions.keys()].filter((u) => u !== EVERYONE);
    if (everyone) {
      const sess0 = S.storeKey; const ctx0 = ctxOf();
      if (!(await confirmEveryone(isReply))) return;
      // The dialog is an await: the login, the open thread or the view may have
      // changed underneath it (COD Code Reviewer) — then send nothing.
      if (S.storeKey !== sess0 || ctxOf() !== ctx0 || activeTextarea() !== ta) return;
    }
    if (S.busy) return;
    // TASK-9835 D — "Also text it" only when the box is visible AND ticked for
    // THIS audience; a refusal the preview already knows is said here, before
    // anything is saved (the server refuses the same way regardless).
    const alsoText = wantsText();
    if (alsoText && S.tp.data && S.tp.data.blocked) { if (err) err.textContent = S.tp.data.blocked; return; }
    // Quiet hours (Chris 2026-10-26): 7 PM-6 AM company time, a texted note
    // needs one extra tap. Never blocks, never schedules; the note alone never warns.
    const _q = alsoText && window.SendFeedback;
    const _qm = _q ? _q.quietHoursWarning({ nowMs: Date.now(), tz: _q.companyTz(window.AppState?.tenantData), count: ((S.tp.data && S.tp.data.canText) || []).length }) : null;
    if (_qm && !_q.quietConfirm('note|' + ctxOf() + '|' + text, Date.now())) {
      if (err) err.textContent = _qm + ' Tap Send anyway to go ahead.';
      const b0 = document.querySelector('#dd-notes-modal [data-tn="send"]');
      if (b0) b0.innerHTML = '<span class="material-icons">send</span>Send anyway';
      return;
    }
    const btn = document.querySelector('#dd-notes-modal [data-tn="send"]');
    if (window.SendFeedback?.isBusy(btn)) return;
    S.busy = true;
    if (btn) btn.disabled = true;
    // TASK-10112 — DURING: steps + elapsed on the composer (never a frozen
    // button, @everyone included). Texts add credit + send steps.
    const SF = window.SendFeedback;
    const statusEl = document.getElementById(isReply ? 'dd-r-status' : 'dd-c-status');
    if (statusEl) statusEl.hidden = false;
    const run = SF && statusEl ? SF.createRun({
      host: statusEl, button: btn,
      total: alsoText ? ((S.tp.data && S.tp.data.canText) || []).length : 0,
      steps: alsoText ? ['Saving note', 'Checking credits', 'Sending texts', 'Done'] : [everyone ? 'Sending to everyone' : 'Sending', 'Done'],
      busyLabel: 'Sending...',
    }) : null;
    const stageTimers = alsoText && run ? [setTimeout(() => run.step(1), 700), setTimeout(() => run.step(2), 1800)] : [];
    // Clear the box BEFORE the call: the listener's snapshot can land (and
    // re-render + restore drafts) before the callable promise resumes, so
    // clearing the pre-await node afterwards would leave the sent text in the
    // new box, ready to post twice (delta COD). On failure it comes back.
    ta.value = '';
    hidePick();
    const ctx = isReply ? S.open : 'compose';
    // TASK-10112 (COD) — one id per message: a retry of the SAME text in the
    // same place (after a lost answer) re-uses it, so the server never sends twice.
    // COD on2124: the audience is part of "the same send" — a changed To: line is a new request.
    const aud = JSON.stringify([!!everyone, mentionUids.slice().sort()]);
    const same = S.pendingReq && S.pendingReq.ctx === ctx && S.pendingReq.text === text && S.pendingReq.alsoText === alsoText && S.pendingReq.aud === aud;
    const requestId = same ? S.pendingReq.id : ('n' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12));
    S.pendingReq = { ctx, text, alsoText, aud, id: requestId };
    // COD on2124: "still going out" keeps the id (a re-send of the same note never mints a second
    // request); "texts unknown" says so instead of reading as a clean send.
    const afterDup = (res) => {
      if (!(res && res.stillSending)) S.pendingReq = null;
      if (res && res.duplicate) toast(res.stillSending ? 'That note is already going out — it will not be sent twice.' : 'Already sent — it was not sent twice.', 'info');
      if (res && res.textUnknown && !res.text) toast('The note was already saved. Its texts may not all have gone out — check with your crew before texting it again.', 'warning');
    };
    const sess = S.storeKey;
    const stale = () => S.storeKey !== sess;
    const sentMentions = [...S.mentions.entries()];
    // Chips picked while the call is in flight belong to the NEXT message: on
    // success drop only the uids that were just sent (COD Code Reviewer).
    const sentKeys = everyone ? [...mentionUids, EVERYONE] : mentionUids;
    const dropSent = (arr) => (arr || []).filter((m) => !sentKeys.includes(m[0]));
    delete S.drafts[ctx];
    persist();
    // TASK-10112 — AFTER, for a texted note: the result card on the page (the
    // panel re-renders on the note's own snapshot, so it cannot live in it).
    const showTextResult = (res) => {
      if (!res || !res.text) return;
      const sf = window.SendFeedback;
      if (sf) { const r = sf.fromNoteText(res.text); if (run) run.finish(r); sf.showResultCard(r, { id: 'dd-note-text-result', title: 'Note texts' }); }
      else toast(`Texted ${Number(res.text.sent) || 0} of ${Number(res.text.total) || 0}.`, (res.text.failures || []).length ? 'warning' : 'success');
    };
    try {
      if (isReply) {
        const id = S.open;
        const res = await call({ op: 'reply', id, text, mentionUids, everyone, requestId, ...(alsoText ? { alsoText: true } : {}) });
        if (stale()) return;
        afterDup(res);
        if (S.drafts[id]) S.drafts[id].mentions = dropSent(S.drafts[id].mentions); // typed during the call: keep it, minus the sent chips
        if (ctxOf() === id) { sentKeys.forEach((u) => S.mentions.delete(u)); refreshChips(); }
        persist();
        if (alsoText) { S.tp.checked = false; showTextResult(res); }
      } else {
        const res = await call({ op: 'create', text, mentionUids, everyone, requestId, ...(alsoText ? { alsoText: true } : {}) });
        if (stale()) return;
        afterDup(res);
        sentKeys.forEach((u) => S.mentions.delete(u));
        if (S.drafts.compose) S.drafts.compose.mentions = dropSent(S.drafts.compose.mentions); // typed during the call: keep it, minus the sent chips
        if (!(res && res.duplicate)) toast(everyone ? 'Note sent to everyone.' : (mentionUids.length ? 'Note sent.' : 'Note saved.'), 'success');
        if (alsoText) { S.tp.checked = false; showTextResult(res); }
        if (res && res.id) { S.view = 'thread'; S.open = res.id; loadMentions(res.id); }
        else S.view = 'list';
        persist();
        renderPanel();
      }
    } catch (e) {
      if (stale()) return;
      if (!(S.drafts[ctx] && S.drafts[ctx].text.trim())) setDraft(ctx, text, sentMentions);
      persist();
      const back = activeTextarea();
      if (back && !back.value && (back.dataset.ctx || '') === ctx) back.value = text;
      const err2 = document.getElementById(isReply ? 'dd-r-err' : 'dd-c-err');
      // TASK-10112 (COD) — a lost ANSWER is not a failed send: the note (and
      // its texts) may already be out. Say so; Send again re-uses the same
      // requestId, so the server answers with the first result, never twice.
      const lost = /deadline-exceeded|unavailable/.test(String(e && e.code || ''));
      const msg = lost ? 'Lost contact while sending — it may already be sent. Check your notes before changing anything; pressing Send again will not send it twice.' : friendly(e);
      if (err2) err2.textContent = msg; else toast(msg, 'error');
    } finally {
      stageTimers.forEach((t) => clearTimeout(t));
      if (run) run.dispose();   // no-op after finish; a plain note just clears its steps
      // The live steps were for the wait; the outcome is the toast / the text
      // result card (which outlives the panel's re-render). Clear the steps.
      const st = document.getElementById(isReply ? 'dd-r-status' : 'dd-c-status');
      if (st) { st.innerHTML = ''; st.style.display = ''; st.style.borderLeft = ''; st.hidden = true; delete st.dataset.sfState; }
      S.busy = false;
      const b2 = document.querySelector('#dd-notes-modal [data-tn="send"]');
      if (b2) b2.disabled = false;
    }
  }

  // @everyone is confirmed with the count before anything is sent (COD).
  // The count is the client's view of who holds Notes (loadPeople) + you; the
  // server resolves the real list at send time.
  async function confirmEveryone(isReply) {
    const n = S.pickable.length + 1;
    const msg = isReply ? `This adds all ${n} people on Notes to this note — they will see the whole thread, not just this reply. Send?`
      : `This goes to all ${n} people on Notes — send?`;
    if (typeof window.showConfirm === 'function') {
      try { return !!(await window.showConfirm('Send to everyone?', msg, { confirmText: 'Send to everyone', icon: 'groups' })); }
      catch (e) { /* fall through to the native dialog */ }
    }
    return window.confirm(msg);
  }

  async function archive(on) {
    const t = find(S.open);
    if (!t || S.busy) return;
    S.busy = true;
    try {
      await call({ op: on ? 'archive' : 'unarchive', id: t.id });
      t.archivedBy = { ...(t.archivedBy || {}), [me()]: on };
      if (!on) delete t.archivedBy[me()];
      toast(on ? 'Archived for you. Everyone else still has it.' : 'Back in your list.', 'success');
      S.view = 'list'; S.open = null;
      persist();
      render();
    } catch (e) { toast(friendly(e), 'error'); }
    finally { S.busy = false; }
  }

  async function saveEdit(messageId) {
    const t = find(S.open);
    const ta = document.getElementById('dd-edit-ta');
    if (!t || !ta || S.busy) return;
    const text = String(ta.value || '').trim();
    if (!text) return;
    S.busy = true;
    try {
      await call({ op: 'edit', id: t.id, messageId, text });
      S.editing = null;
    } catch (e) { toast(friendly(e), 'error'); }
    finally { S.busy = false; renderPanel(); }
  }

  // --------------------------------------------------------------------------
  // Styles (self-contained: no styles.css buster to keep in sync)
  // --------------------------------------------------------------------------
  function injectCss() {
    if (document.getElementById('dd-notes-css')) return;
    const s = document.createElement('style');
    s.id = 'dd-notes-css';
    s.textContent = `
      .dd-brand-tools{display:flex;justify-content:flex-end;align-items:center;gap:6px;margin-top:6px}
      .dd-brand-tool{position:relative;display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;padding:0;border-radius:8px;
        border:1px solid rgba(255,255,255,.18);background:transparent;color:rgba(255,255,255,.78);cursor:pointer;transition:background .15s ease,color .15s ease,border-color .15s ease}
      .dd-brand-tool > svg{display:block}
      .dd-brand-tool:hover{background:rgba(255,255,255,.08);color:#fff;border-color:rgba(255,255,255,.32)}
      .dd-brand-tool:focus-visible{outline:2px solid var(--accent-cyan,#06B6D4);outline-offset:2px}
      .dd-notes-orb{position:relative;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;width:32px;height:32px;padding:0;border-radius:50%;
        border:1px solid rgba(252,211,77,.7);background:radial-gradient(circle at 32% 30%,#FDE68A 0%,#FBBF24 45%,#F59E0B 100%);color:#78350F;cursor:pointer;
        box-shadow:0 2px 8px rgba(245,158,11,.35);transition:transform .15s ease,box-shadow .15s ease}
      .dd-notes-orb > .material-icons{font-size:18px}
      .dd-notes-orb:hover{transform:translateY(-1px)}
      .dd-notes-orb:focus-visible{outline:2px solid #fff;outline-offset:3px}
      .dd-notes-orb.has-unread{box-shadow:0 0 0 3px rgba(245,158,11,.28),0 2px 14px rgba(245,158,11,.6);animation:dd-notes-glow 2.4s ease-in-out infinite}
      @keyframes dd-notes-glow{0%,100%{box-shadow:0 0 0 3px rgba(245,158,11,.22),0 2px 10px rgba(245,158,11,.45)}50%{box-shadow:0 0 0 5px rgba(245,158,11,.34),0 2px 18px rgba(245,158,11,.7)}}
      .dd-notes-orb-n{position:absolute;top:-6px;right:-7px;min-width:17px;height:17px;padding:0 4px;border-radius:999px;background:#fff;color:var(--brand-navy);font-size:10px;font-weight:800;line-height:17px;text-align:center;box-shadow:0 0 0 2px var(--brand-navy)}
      .dd-notes-orb-n[hidden]{display:none}
      .dd-notes-peek{position:fixed;width:300px;display:flex;flex-direction:column;gap:4px;padding:10px 12px;background:#fff;color:var(--text-primary);border:1px solid var(--border-light);border-radius:10px;
        box-shadow:0 12px 28px rgba(15,23,42,.2);font-size:12.5px;line-height:1.45;opacity:0;visibility:hidden;transform:translateY(-4px);transition:opacity .12s ease,transform .12s ease,visibility .12s;pointer-events:none;z-index:1200}
      .dd-notes-peek.on{opacity:1;visibility:visible;transform:none}
      .dd-notes-peek .pk-h{font-size:11px;font-weight:700;color:var(--text-secondary)}
      .dd-notes-peek .pk-b{white-space:pre-wrap;word-break:break-word;display:-webkit-box;-webkit-line-clamp:4;-webkit-box-orient:vertical;overflow:hidden}
      .dd-notes-peek .pk-empty{color:var(--text-secondary)}
      .dd-notes-peek .pk-f{font-size:11px;color:var(--text-secondary);border-top:1px solid var(--border-light);padding-top:5px;margin-top:2px}
      @media (prefers-reduced-motion: reduce){.dd-brand-tool,.dd-notes-orb,.dd-notes-peek{transition:none}.dd-notes-orb:hover{transform:none}.dd-notes-orb.has-unread{animation:none}}
      .dd-notes-nav-badge{display:inline-flex;align-items:center;justify-content:center;min-width:18px;height:18px;padding:0 5px;margin-left:6px;border-radius:9px;background:#B45309;color:#fff;font-size:11px;font-weight:700}
      .dd-notes-more-dot{position:absolute;top:6px;right:calc(50% - 14px);width:9px;height:9px;border-radius:50%;background:#F59E0B;box-shadow:0 0 0 2px #fff}
      #mobile-nav-more{position:relative}
      #dd-notes-modal{z-index:1060}
      .dd-notes-content{max-width:760px;width:100%;height:min(82vh,760px);display:flex;flex-direction:column}
      .dd-notes-head h3{display:flex;align-items:center;gap:8px;margin:0}
      .dd-notes-head h3 .material-icons{color:var(--text-secondary)}
      .dd-notes-head-actions{display:flex;align-items:center;gap:8px}
      .dd-notes-head-actions .btn{display:inline-flex;align-items:center;gap:4px}
      .dd-notes-head-actions .btn .material-icons{font-size:16px}
      .dd-notes-body{flex:1;min-height:0;display:flex;flex-direction:column;background:var(--bg-card)}
      .dd-tabs{display:flex;align-items:center;gap:4px;padding:8px 12px;border-bottom:1px solid var(--border-light);flex-wrap:wrap}
      .dd-tab{border:none;background:transparent;color:var(--text-secondary);font-weight:600;padding:6px 10px;border-radius:999px;cursor:pointer;display:inline-flex;align-items:center;gap:6px;min-height:36px}
      .dd-tab.on{background:rgba(59,130,246,.12);color:var(--brand-blue)}
      .dd-tab-n{min-width:18px;height:18px;padding:0 5px;border-radius:9px;background:#B45309;color:#fff;font-size:11px;font-weight:700;display:inline-flex;align-items:center;justify-content:center}
      .dd-arch-toggle{margin-left:auto;font-size:12.5px;color:var(--text-secondary);display:flex;align-items:center;gap:6px;cursor:pointer}
      .dd-list,.dd-scroll{flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch}
      .dd-row{display:flex;align-items:flex-start;gap:10px;width:100%;text-align:left;padding:10px 14px;border:none;border-bottom:1px solid var(--border-light);background:transparent;cursor:pointer;color:var(--text-primary);position:relative}
      .dd-row:hover{background:var(--bg-primary)}
      .dd-row.archived{opacity:.6}
      .dd-row-av{flex-shrink:0;width:32px;height:32px;border-radius:50%;background:var(--bg-elevated);color:var(--text-primary);font-weight:700;display:inline-flex;align-items:center;justify-content:center;font-size:14px}
      .dd-row-main{flex:1;min-width:0;display:flex;flex-direction:column;gap:2px}
      .dd-row-top{display:flex;align-items:baseline;justify-content:space-between;gap:8px}
      .dd-row-who{font-weight:600;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
      .dd-row-when{font-size:12px;color:var(--text-secondary);flex-shrink:0}
      .dd-row-prev{font-size:13px;color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
      .dd-row.unread .dd-row-who,.dd-row.unread .dd-row-prev{font-weight:700;color:var(--text-primary)}
      .dd-row-dot{flex-shrink:0;width:9px;height:9px;border-radius:50%;background:#F59E0B;margin-top:12px}
      .dd-empty{padding:28px 20px;color:var(--text-secondary);text-align:center;line-height:1.5}
      .dd-empty-sub{font-size:13px}
      .dd-sub{display:flex;align-items:center;gap:8px;padding:8px 12px;border-bottom:1px solid var(--border-light);flex-wrap:wrap}
      .dd-back{display:inline-flex;align-items:center;gap:2px;border:none;background:transparent;color:var(--brand-blue);font-weight:600;cursor:pointer;padding:6px 8px 6px 2px;min-height:36px}
      .dd-back .material-icons{font-size:18px}
      .dd-sub-title{font-weight:700;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;min-width:0}
      .dd-arch{display:inline-flex;align-items:center;gap:4px}
      .dd-arch .material-icons{font-size:16px}
            .dd-chip{display:inline-flex;align-items:center;gap:2px;padding:3px 10px;border-radius:999px;background:linear-gradient(135deg,var(--brand-blue) 0%,var(--brand-cyan) 100%);color:#fff;font-size:12.5px;font-weight:600;line-height:1.4}
      .dd-chip.fixed{opacity:.85}
      .dd-chip.everyone{background:linear-gradient(135deg,var(--brand-navy) 0%,var(--brand-blue) 100%)}
      .dd-chip-x{border:none;background:transparent;color:inherit;cursor:pointer;font-size:15px;line-height:1;padding:0 0 0 4px}
      .dd-mention{color:var(--brand-blue);font-weight:700}
      .dd-msgs{padding:10px 14px;display:flex;flex-direction:column;gap:10px}
      .dd-msg{max-width:min(92%,640px);padding:8px 12px;border-radius:12px;background:var(--bg-primary);border:1px solid var(--border-light);align-self:flex-start}
      .dd-msg.mine{align-self:flex-end;background:#EFF6FF;border-color:#BFDBFE}
      .dd-msg.editing{width:100%;max-width:100%}
      .dd-msg-meta{font-size:12px;color:var(--text-secondary);margin-bottom:3px}
      .dd-msg-meta strong{color:var(--text-primary)}
      .dd-msg-text{color:var(--text-primary);white-space:normal;overflow-wrap:anywhere;line-height:1.45}
      .dd-msg-actions{display:flex;justify-content:flex-end;gap:6px;margin-top:6px}
      .dd-link{border:none;background:transparent;color:var(--brand-blue);cursor:pointer;font-size:12px;padding:0 4px;font-weight:600}
      .dd-composer{padding:10px 12px 12px;border-top:1px solid var(--border-light);background:var(--bg-card)}
      .dd-compose-wrap{padding:12px}
      .dd-compose-wrap .dd-composer{border-top:none;padding:0}
      .dd-to{display:flex;flex-wrap:wrap;align-items:center;gap:6px;margin-bottom:6px;padding:8px 10px;border:1px solid var(--border-light);border-radius:var(--radius-md);background:var(--bg-primary);min-height:40px}
      .dd-to-label{font-weight:700;color:var(--text-primary);margin-right:2px}
      .dd-to-me{color:var(--text-secondary);font-style:italic}
      .dd-to-add{display:inline-flex;align-items:center;gap:4px;margin-left:auto;border:1px dashed var(--brand-blue);background:transparent;color:var(--brand-blue);font-weight:600;font-size:12.5px;padding:4px 10px;border-radius:999px;cursor:pointer;min-height:30px}
      .dd-to-add .material-icons{font-size:16px}
      .dd-to-add:hover{background:rgba(59,130,246,.08)}
      .dd-ta-wrap{position:relative}
      .dd-ta{width:100%;resize:vertical;min-height:72px;padding:10px;border:1px solid var(--border-light);border-radius:var(--radius-md);font:inherit;color:var(--text-primary);background:#fff;box-sizing:border-box}
      .dd-ta:focus{outline:none;border-color:var(--brand-blue);box-shadow:0 0 0 3px rgba(59,130,246,.18)}
      .dd-pick{position:fixed;top:0;left:0;min-width:220px;max-width:min(320px,calc(100vw - 16px));background:#fff;border:1px solid var(--border-light);border-radius:10px;box-shadow:0 12px 28px rgba(15,23,42,.18);padding:4px;z-index:1070;max-height:240px;overflow-y:auto;-webkit-overflow-scrolling:touch;box-sizing:border-box}
      .dd-draft-row .dd-row-who{color:var(--brand-blue)}
      .dd-pick.hidden{display:none}
      .dd-pick-item{display:flex;align-items:center;gap:8px;width:100%;text-align:left;border:none;background:transparent;padding:6px 8px;border-radius:8px;cursor:pointer;color:var(--text-primary);font-weight:600;min-height:36px}
      .dd-pick-item .dd-row-av{width:26px;height:26px;font-size:12px}
      .dd-pick-item.on,.dd-pick-item:hover{background:rgba(59,130,246,.12)}
      .dd-pick-item.everyone{border-bottom:1px solid var(--border-light);border-radius:8px 8px 0 0;margin-bottom:2px}
      .dd-pick-sub{margin-left:auto;font-size:11px;font-weight:500;color:var(--text-secondary);padding-left:8px}
      .dd-pick-q{width:100%;box-sizing:border-box;padding:7px 9px;margin-bottom:4px;border:1px solid var(--border-light);border-radius:8px;font:inherit;font-size:13px;color:var(--text-primary);background:#fff}
      .dd-pick-q:focus{outline:none;border-color:var(--brand-blue);box-shadow:0 0 0 3px rgba(59,130,246,.18)}
      .dd-pick-empty{padding:8px 10px;color:var(--text-secondary);font-size:12.5px}
      .dd-composer-foot{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:8px;flex-wrap:wrap}
      .dd-composer-foot .btn{display:inline-flex;align-items:center;gap:4px}
      .dd-composer-foot .btn .material-icons{font-size:16px}
      .dd-hint{font-size:12.5px;color:var(--text-secondary);margin:0 0 6px 2px;line-height:1.4}
      .dd-foot-note{font-size:12px;color:var(--text-secondary)}
      .dd-err{color:var(--status-danger);font-size:0.85rem;margin-top:6px;min-height:1em}
      .dd-foot-note{flex:1 1 auto}
      .dd-memo-ptr{display:flex;flex-wrap:wrap;align-items:center;gap:4px;margin-top:12px;padding-top:10px;border-top:1px solid var(--border-light);font-size:12.5px}
      .dd-memo-ptr .dd-link{font-size:12.5px;min-height:44px}
      @media (max-width:768px){
        #dd-notes-modal{padding:0 !important;align-items:stretch !important}
        #dd-notes-modal .dd-notes-content{width:100% !important;max-width:100% !important;height:100vh !important;height:100dvh !important;max-height:100vh !important;max-height:100dvh !important;border-radius:0 !important;border:none !important;margin:0 !important}
        .dd-pick-item{min-height:44px}
        .dd-chip-x{min-width:32px;min-height:32px;margin:-6px -8px -6px 0;font-size:18px}
        .dd-notes-head-actions .btn span:not(.material-icons){display:none}
        .dd-foot-note{display:none}
        .dd-msg{max-width:96%}
      }`;
    document.head.appendChild(s);
  }

  window.TeamNotes = {
    start,
    stop,
    open,
    openThread,
    close,
    canUse,
    _memo: { canSend: canSendMemo, html: memoPointerHtml, open: openMemo }, // TASK-10142
    // test hooks (read-only views of state + the draft helpers, exercised
    // against a fake document by tests/task9619-team-notes.unit.cjs)
    _state: () => ({ count: S.threads.length, unread: unreadThreads().length, pickable: S.pickable.length, view: S.view, busy: S.busy, mentions: [...S.mentions.keys()] }),
    _drafts: { snapshot: snapshotDrafts, restore: restoreDrafts, parseStore, storeKey: draftStoreKey, sweep: sweepDrafts },
    _mentions: { tokens: mentionTokens, unmatched: unmatchedMentions, strip: stripMention, EVERYONE },
  };
})();

function __ddChunkWrap(p){var h=globalThis.__ddChunk;return typeof h==="function"?h(p):p}
